import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { CANVAS_PAD, PAGE_GAP, usePagePreviewScale } from "../../shared/usePagePreviewScale";
import PagePreviewPages from "../../shared/PagePreviewPages";
import AtsCvPreview, { ATS_CV_PAGE_WIDTH } from "./AtsCvPreview.jsx";
import { ATS_CV_PAGE_MIN_HEIGHT } from "../engines/atsCvLayout.js";

/**
 * The CV inside the preview rail.
 *
 * Same canvas as the printed-form tools: the sheet is fitted to the card so a
 * whole page is on screen at once, scrolling moves to the next page, and past
 * fitted size the paper is dragged around rather than scrolled. The zoom itself
 * lives in the panel header — this only reads the number.
 *
 * Pages are a fixed height here, so how far down page two starts is arithmetic
 * rather than a measurement; only how MANY there are has to be counted, and the
 * paginator that decides that runs inside `AtsCvPreview`.
 */
export default function AtsCvPreviewFrame({ form, previewRef, template = "classic" , zoom = 1 }) {
    const rootRef = useRef(null);
    const [pageCount, setPageCount] = useState(1);
    const [scrollRef, scale, page] = usePagePreviewScale(
        ATS_CV_PAGE_WIDTH,
        ATS_CV_PAGE_MIN_HEIGHT,
        zoom,
        pageCount,
    );

    const countPages = useCallback(() => {
        const root = rootRef.current;
        if (!root) return;
        const sheets = root.querySelectorAll(".ats-cv-page-sheet");
        setPageCount(Math.max(1, sheets.length));
    }, []);

    const assignPreviewRef = useCallback(
        (node) => {
            rootRef.current = node;
            if (typeof previewRef === "function") {
                previewRef(node);
            } else if (previewRef) {
                previewRef.current = node;
            }
            if (node) countPages();
        },
        [previewRef, countPages],
    );

    // The page count follows the text: one more line of experience can push the
    // CV onto a second sheet, and the pagination runs a frame after the form
    // changes, so this has to re-read rather than compute.
    useLayoutEffect(() => {
        countPages();
    }, [form, template, countPages]);

    const documentHeight = pageCount * ATS_CV_PAGE_MIN_HEIGHT + (pageCount - 1) * PAGE_GAP;

    return (
        // The page marker is pinned to the card, not to the paper, so it stays
        // put while the pages scroll under it.
        <div className="relative flex min-h-0 w-full flex-1">
            <div
                ref={scrollRef}
                className="ats-cv-preview-canvas w-full min-h-0 flex-1 select-none overflow-auto rounded-lg bg-[#3d3d3d]"
                style={{ padding: CANVAS_PAD }}
            >
                {/* The scaled paper is laid out at full size, so the scroll box
                    is given the height the transform actually paints. */}
                <div
                    style={{
                        height: documentHeight * scale,
                        width: ATS_CV_PAGE_WIDTH * scale,
                        margin: "0 auto",
                    }}
                >
                    <div
                        style={{
                            width: ATS_CV_PAGE_WIDTH,
                            transform: `scale(${scale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        <AtsCvPreview
                            ref={assignPreviewRef}
                            form={form}
                            template={template}
                            activePreviewPage={null}
                        />
                    </div>
                </div>
            </div>
            <PagePreviewPages page={page} total={pageCount} />
        </div>
    );
}
